import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

type SentryModule = typeof import('@sentry/react');
type SentryUser = Parameters<SentryModule['setUser']>[0];
type SentryCaptureContext = Parameters<SentryModule['captureException']>[1];
type WindowWithIdleCallback = typeof window & {
    requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number;
};

let sentryPromise: Promise<SentryModule> | null = null;
let sentryReady = false;
let pendingUser: SentryUser | undefined;

function loadSentry() {
    if (!sentryPromise) {
        sentryPromise = import('@sentry/react');
    }
    return sentryPromise;
}

export function initSentry() {
    const start = () => {
        loadSentry().then(async Sentry => {
            Sentry.init({
                dsn: 'https://ea90a0454c8a1a2ed7711f8daaf2c16d@o4511349863940096.ingest.de.sentry.io/4511349877243984',
                environment: import.meta.env.MODE,
                sendDefaultPii: true,
                integrations: [
                    Sentry.reactRouterV6BrowserTracingIntegration({
                        useEffect,
                        useLocation,
                        useNavigationType,
                        createRoutesFromChildren,
                        matchRoutes,
                    }),
                    Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
                ],
                tracesSampleRate: 0.2,
                replaysSessionSampleRate: 0,
                replaysOnErrorSampleRate: 1.0,
            });
            sentryReady = true;
            if (pendingUser !== undefined) {
                Sentry.setUser(pendingUser);
            }
        }).catch(() => {});
    };

    const browserWindow = window as WindowWithIdleCallback;
    if (browserWindow.requestIdleCallback) {
        browserWindow.requestIdleCallback(start, { timeout: 3000 });
    } else {
        window.setTimeout(start, 1200);
    }
}

export function setSentryUser(user: SentryUser) {
    pendingUser = user;
    if (!sentryReady) return;
    loadSentry().then(Sentry => Sentry.setUser(user)).catch(() => {});
}

export function captureSentryException(error: unknown, context?: SentryCaptureContext) {
    loadSentry()
        .then(Sentry => Sentry.captureException(error, context))
        .catch(() => {});
}
